/**
 * The shell's own reads — which menus are open, and what a project has — as
 * ONE definition each: one query key, one fetcher, one freshness.
 *
 * WHY THIS EXISTS (QA pass, 2026-10-28)
 *
 * These three endpoints were read from eight places, each with its own
 * `useQuery` options. The sidebar asked with `staleTime: 0, refetchOnMount:
 * true` and `refetchOnWindowFocus: true`; the tab bar and the page tab strips
 * asked with `staleTime: 0`; the menu gate with a minute. React Query applies
 * each OBSERVER's options when it mounts, so every page that mounted a tab
 * strip refetched all three, and every window focus refetched them again —
 * menu-visibility and features were fetched twice per navigation. Together
 * with the dashboard's focus refetch that was enough to reach the per-user
 * limit in ordinary use ("Too many requests", and once a 429 on a save).
 *
 * What these answer changes only when an administrator flips a switch, and
 * every switch already invalidates its key (Platform Settings › Features,
 * Project Settings › Features & Billing, the project dialog), so a long
 * freshness costs nothing on the device that made the change. Another
 * person's device picks it up within SHELL_STALE_MS.
 *
 * One fetcher, too. Two fetchers on one key disagree about failure — the first
 * observer to fetch decides — and these swallowed every error into `{}`/`null`
 * and cached it as if it were the answer. Now a 403 (not a member) is `null`,
 * as before; anything else is an error the caller reads as "no data yet",
 * kept quiet (`meta.quiet`) because the sidebar has nothing useful to say
 * about it, and retried by the cache's own rules rather than cached for
 * minutes as an empty answer.
 */
import { ApiError } from "./apiError";
import { fetchJson } from "./queryClient";

/** How long the shell trusts what it was told. */
export const SHELL_STALE_MS = 5 * 60_000;

type Features = {
  addonFeatures?: Array<{ key: string; isEnabled: boolean }>;
  showroom?: boolean;
  agentPortal?: boolean;
  emailMarketing?: boolean;
  signingOfficer?: boolean;
  [key: string]: unknown;
};

const shell = {
  staleTime: SHELL_STALE_MS,
  refetchOnWindowFocus: false,
  meta: { quiet: true },
} as const;

/** A 403 is an answer ("not on this project"), not a failure. */
async function nullOn403<T>(read: Promise<T>): Promise<T | null> {
  try {
    return await read;
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) return null;
    throw err;
  }
}

/** Platform-wide menu switches (Platform Settings › Features). */
export function menuVisibilityQuery() {
  return {
    ...shell,
    queryKey: ["/api/menu-visibility"] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      fetchJson<Array<{ menuKey: string; visible: boolean; chip?: string | null; [key: string]: unknown }>>("/api/menu-visibility", { signal, background: true }),
  };
}

/** One project's own menu switches. */
export function projectMenuVisibilityQuery(projectId: string | null | undefined, enabled = true) {
  return {
    ...shell,
    queryKey: ["/api/projects", projectId, "menu-visibility"] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      nullOn403(fetchJson<Record<string, boolean>>(`/api/projects/${projectId}/menu-visibility`, { signal, background: true })),
    enabled: enabled && !!projectId,
  };
}

/** What a project (and its company) has: add-ons, Showroom, Agent Portal, Email Marketing… */
export function projectFeaturesQuery(projectId: string | null | undefined, enabled = true) {
  return {
    ...shell,
    queryKey: ["/api/projects", projectId, "features"] as const,
    queryFn: ({ signal }: { signal?: AbortSignal }) =>
      nullOn403(fetchJson<Features>(`/api/projects/${projectId}/features`, { signal, background: true })),
    enabled: enabled && !!projectId,
  };
}
